import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

/**
 * 点云变换控制接口 - 组件内聚的类型定义
 */
export interface PointCloudTransform {
  // 尺寸控制
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  // 位置控制
  positionX: number;
  positionY: number;
  positionZ: number;
}

/**
 * 默认变换配置
 */
export const DEFAULT_TRANSFORM: PointCloudTransform = {
  scaleX: 1.0,
  scaleY: 1.0,
  scaleZ: 1.0,
  positionX: 0.0,
  positionY: 0.0,
  positionZ: 0.0,
};

interface PointCloudControlsProps {
  transform: PointCloudTransform;
  onChange: (transform: PointCloudTransform) => void;
}

export function PointCloudControls({
  transform,
  onChange,
}: PointCloudControlsProps) {
  const updateTransform = (key: keyof PointCloudTransform, value: number) => {
    onChange({
      ...transform,
      [key]: value,
    });
  };

  const SliderControl = ({
    label,
    value,
    onChange,
    min,
    max,
    step = 0.1,
    unit = "",
  }: {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step?: number;
    unit?: string;
  }) => (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-xs font-normal text-muted-foreground">
          {label}
        </Label>
        <span className="text-xs text-muted-foreground font-mono">
          {value.toFixed(2)}
          {unit}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(values) => onChange(values[0])}
        min={min}
        max={max}
        step={step}
        disabled={false}
        className="w-full"
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Size Control */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 mb-2">Size Control</h4>
        <div className="space-y-3">
          <SliderControl
            label="Width (X)"
            value={transform.scaleX}
            onChange={(value) => updateTransform("scaleX", value)}
            min={0.1}
            max={3.0}
            step={0.1}
            unit="x"
          />
          <SliderControl
            label="Height (Y)"
            value={transform.scaleY}
            onChange={(value) => updateTransform("scaleY", value)}
            min={0.1}
            max={3.0}
            step={0.1}
            unit="x"
          />
          <SliderControl
            label="Depth (Z)"
            value={transform.scaleZ}
            onChange={(value) => updateTransform("scaleZ", value)}
            min={0.1}
            max={3.0}
            step={0.1}
            unit="x"
          />
        </div>
      </div>

      <Separator />

      {/* Position Control */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 mb-2">
          Position Control
        </h4>
        <div className="space-y-3">
          <SliderControl
            label="Left/Right (X)"
            value={transform.positionX}
            onChange={(value) => updateTransform("positionX", value)}
            min={-1.0}
            max={1.0}
            step={0.05}
            unit=""
          />
          <SliderControl
            label="Up/Down (Y)"
            value={transform.positionY}
            onChange={(value) => updateTransform("positionY", value)}
            min={-1.0}
            max={1.0}
            step={0.05}
            unit=""
          />
          <SliderControl
            label="Front/Back (Z)"
            value={transform.positionZ}
            onChange={(value) => updateTransform("positionZ", value)}
            min={-1.0}
            max={1.0}
            step={0.05}
            unit=""
          />
        </div>
      </div>
    </div>
  );
}
